/**
 * PATH: packages/ui/src/kanban/api-fetch/cardLifecycle.ts
 * FILE_ID: UI-KANBAN-FETCH-CARDLIFECYCLE-001
 * PURPOSE: Own card merge, audit-revert, and mirror response normalization.
 * WHY: Lifecycle endpoints can return cards that must cross the same wire boundary.
 * FLOW: lifecycle request -> raw response -> normalized Card plus narrow metadata.
 * DEPENDENCIES: ApiFetchContext; cardWireBoundary.
 * RELATED: cards.ts; components/KanbanMergeCardsDialog.tsx.
 */
import type { KanbanApi } from '../api'
import {
  normalizeCardWireForRenderer,
} from './cardWireBoundary'
import type { ApiFetchContext } from './context'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function normalizeOptionalCard(
  value: unknown,
  endpoint: string,
) {
  return isRecord(value) && value.card !== undefined
    ? normalizeCardWireForRenderer(value.card, endpoint)
    : undefined
}

export function createCardLifecycleApi(ctx: ApiFetchContext) {
  const { _request } = ctx
  return {
    mergeCards: async (keeperId, loserIds, reason) => {
      const endpoint = `POST /cards/${keeperId}/merge/`
      const response = await _request<unknown>(
        'POST',
        `/cards/${keeperId}/merge/`,
        { loser_ids: loserIds, reason },
      )
      if (!isRecord(response) || typeof response.id !== 'string' || !Array.isArray(response.merged)) {
        throw new Error(`Malformed merge response from ${endpoint}`)
      }
      return {
        id: response.id,
        merged: response.merged.filter((id): id is string => typeof id === 'string'),
        ...(typeof response.related_emails_added === 'number'
          ? { related_emails_added: response.related_emails_added }
          : {}),
        ...(normalizeOptionalCard(response, endpoint)
          ? { card: normalizeOptionalCard(response, endpoint) }
          : {}),
      }
    },
    unmergeCards: async (keeperId, loserIds, reason) => {
      const endpoint = `POST /cards/${keeperId}/unmerge/`
      const response = await _request<unknown>(
        'POST',
        `/cards/${keeperId}/unmerge/`,
        { loser_ids: loserIds, reason },
      )
      if (!isRecord(response) || typeof response.id !== 'string' || !Array.isArray(response.unmerged)) {
        throw new Error(`Malformed unmerge response from ${endpoint}`)
      }
      return {
        id: response.id,
        unmerged: response.unmerged.filter(
          (id): id is string => typeof id === 'string',
        ),
        ...(Array.isArray(response.skipped)
          ? { skipped: response.skipped.filter((id): id is string => typeof id === 'string') }
          : {}),
        ...(typeof response.related_emails_removed === 'number'
          ? { related_emails_removed: response.related_emails_removed }
          : {}),
        ...(normalizeOptionalCard(response, endpoint)
          ? { card: normalizeOptionalCard(response, endpoint) }
          : {}),
      }
    },
    markAuditReverted: async (cardId, entryRef) => {
      const endpoint = `POST /cards/${cardId}/revert-audit/`
      const response = await _request<unknown>(
        'POST',
        `/cards/${cardId}/revert-audit/`,
        { at: entryRef.at, event: entryRef.event },
      )
      return normalizeCardWireForRenderer(response, endpoint)
    },
    mirrorCardToBoard: async (cardId, input) => {
      const endpoint = `POST /cards/${cardId}/mirror-to-board/`
      const response = await _request<unknown>(
        'POST',
        `/cards/${cardId}/mirror-to-board/`,
        input,
      )
      const card = normalizeCardWireForRenderer(response, endpoint)
      return {
        ...card,
        ...(isRecord(response) && typeof response.mirror_created === 'boolean'
          ? { mirror_created: response.mirror_created }
          : {}),
        ...(isRecord(response) && typeof response.landing_lane_name === 'string'
          ? { landing_lane_name: response.landing_lane_name }
          : {}),
      }
    },
  } satisfies Partial<KanbanApi>
}
